import { Action, combineReducers } from "redux";
import { Action as ReduxAction, handleActions, ReducerMap } from "redux-actions";
import { IAction } from "reduxsauce";
import { Types as AllTypes } from "../actions";
import * as actions from "../actions/dashboard/dashboard.actions";
import { IClass, ICourse, ICourseInstance, IScormPackage, RequestState } from "../App/types/common";

export interface IState {
  _: IDashboardState;
}

export interface IDashboardState {
  analytics: ICourseCompletionAnalytics;
  courseInstances: Nullable<ICourseInstance[]>;
  courses: ICourse[];
  classes: Nullable<IClass[]>;
  editableByUser: string[];
  allCourseIdsOnDashboard: string[];
  fetchAllDashboardDataRequestState: RequestState;
  fetchAllDashboardDataRequestError: Nullable<{ message: string }>;
  fetchCourseCompletionAnalyticsState: RequestState;
  fetchCourseCompletionAnalyticsError: Nullable<string>;
  loadingMoreCoursesState: RequestState;
  scormPackages: Nullable<IScormPackage[]>;
  failedCoursesIds: string[];
}

const Types = (AllTypes as any)._APP._DASHBOARD;

export interface ICourseCompletionAnalytics {
  completed: number;
  completedUsers: number;
  started: number;
  startedUsers: number;
  notStarted: number;
  notStartedUsers: number;
  totalUsers: number;
}

export const INITIAL_STATE: IDashboardState = {
  allCourseIdsOnDashboard: [],
  analytics: {
    completed: 0,
    completedUsers: 0,
    notStarted: 0,
    notStartedUsers: 0,
    started: 0,
    startedUsers: 0,
    totalUsers: 0,
  },
  classes: null,
  courseInstances: null,
  courses: [],
  editableByUser: [],
  failedCoursesIds: [],
  fetchAllDashboardDataRequestError: null,
  fetchAllDashboardDataRequestState: RequestState.IDLE,
  fetchCourseCompletionAnalyticsError: null,
  fetchCourseCompletionAnalyticsState: RequestState.IDLE,
  loadingMoreCoursesState: RequestState.IDLE,
  scormPackages: null,
};

export function mount(state: IDashboardState, action: IAction): IDashboardState {
  return INITIAL_STATE;
}

export function unmount(state: IDashboardState, action: IAction): IDashboardState {
  return INITIAL_STATE;
}

export function fetchAllDashboardData(state: IDashboardState, action: IAction): IDashboardState {
  return {
    ...state,
    fetchAllDashboardDataRequestError: null,
    fetchAllDashboardDataRequestState: RequestState.PENDING,
  };
}

export function handleFetchAllDashboardDataFailure(state: IDashboardState, action: IAction): IDashboardState {
  return {
    ...state,
    fetchAllDashboardDataRequestError: action.error.message,
    fetchAllDashboardDataRequestState: RequestState.FAILED,
  };
}

export function handleFetchAllDashboardDataSuccess(state: IDashboardState, action: IAction): IDashboardState {
  return {
    ...state,
    allCourseIdsOnDashboard: action.editableByUser,
    classes: action.classes,
    courseInstances: action.courseInstances,
    courses: action.courses,
    editableByUser: action.editableByUser,
    failedCoursesIds: action.failedCoursesIds,
    fetchAllDashboardDataRequestError: null,
    fetchAllDashboardDataRequestState: RequestState.SUCCEEDED,
    scormPackages: action.scormPackages,
  };
}

export const fetchCourseCompletionAnalytics = (state: IDashboardState, action: IAction): IDashboardState => ({
  ...state,
  fetchCourseCompletionAnalyticsError: null,
  fetchCourseCompletionAnalyticsState: RequestState.PENDING,
});

export const handleFetchCourseCompletionAnalyticsFailure = (
  state: IDashboardState,
  action: IAction
): IDashboardState => ({
  ...state,
  fetchCourseCompletionAnalyticsError: action.error.message,
  fetchCourseCompletionAnalyticsState: RequestState.FAILED,
});

export const handleFetchCourseCompletionAnalyticsSuccess = (
  state: IDashboardState,
  action: IAction
): IDashboardState => ({
  ...state,
  analytics: action.analytics,
  fetchCourseCompletionAnalyticsError: null,
  fetchCourseCompletionAnalyticsState: RequestState.SUCCEEDED,
});

const loadMoreCourses = (state: IDashboardState): IDashboardState => ({
  ...state,
  loadingMoreCoursesState: RequestState.PENDING,
});

const handleLoadMoreCoursesSuccess = (
  state: IDashboardState,
  { payload }: ReduxAction<actions.ILoadCoursesPayload>
): IDashboardState => ({
  ...state,
  courses: state.courses.concat(payload.courses),
  failedCoursesIds: state.failedCoursesIds.concat(payload.failedCoursesIds),
  loadingMoreCoursesState: RequestState.SUCCEEDED,
});

const searchCoursesByName = (state: IDashboardState): IDashboardState => ({
  ...state,
  loadingMoreCoursesState: RequestState.PENDING,
});

const handleSearchCoursesByNameFailure = (
  state: IDashboardState,
  _: ReduxAction<{ message: string }>
): IDashboardState => ({
  ...state,
  loadingMoreCoursesState: RequestState.FAILED,
});

const handleSearchCoursesByNameSuccess = (
  state: IDashboardState,
  action: ReduxAction<actions.ISearchCoursesPayload>
): IDashboardState => ({
  ...state,
  courses: action.payload.courses,
  editableByUser: action.payload.courseIds,
  failedCoursesIds: action.payload.failedCoursesIds,
  loadingMoreCoursesState: RequestState.SUCCEEDED,
});

const reduxsauceReducers: ReducerMap<IDashboardState, any> = {
  [Types.MOUNT]: mount,
  [Types.FETCH_ALL_DASHBOARD_DATA]: fetchAllDashboardData,
  [Types.FETCH_COURSE_COMPLETION_ANALYTICS]: fetchCourseCompletionAnalytics,
  [Types.HANDLE_FETCH_ALL_DASHBOARD_DATA_FAILURE]: handleFetchAllDashboardDataFailure,
  [Types.HANDLE_FETCH_ALL_DASHBOARD_DATA_SUCCESS]: handleFetchAllDashboardDataSuccess,
  [Types.HANDLE_FETCH_COURSE_COMPLETION_ANALYTICS_FAILURE]: handleFetchCourseCompletionAnalyticsFailure,
  [Types.HANDLE_FETCH_COURSE_COMPLETION_ANALYTICS_SUCCESS]: handleFetchCourseCompletionAnalyticsSuccess,
  [Types.UNMOUNT]: unmount,
};

type DashboardPayload =
  | ICourse[]
  | actions.ISearchCoursesPayload
  | actions.ILoadCoursesPayload
  | { message: string }
  | void;

const reuduxactionsReducers: ReducerMap<IDashboardState, DashboardPayload> = {
  [actions.LOAD_ALL_COURSES]: loadMoreCourses,
  [actions.LOAD_MORE_COURSES]: loadMoreCourses,
  [actions.HANDLE_LOAD_MORE_COURSES_SUCCESS]: handleLoadMoreCoursesSuccess,
  [actions.SEARCH_COURSES_BY_NAME]: searchCoursesByName,
  [actions.HANDLE_SEARCH_COURSES_BY_NAME_FAILURE]: handleSearchCoursesByNameFailure,
  [actions.HANDLE_SEARCH_COURSES_BY_NAME_SUCCESS]: handleSearchCoursesByNameSuccess,
};

export default combineReducers<IState, Action>({
  _: handleActions<IDashboardState, DashboardPayload>(
    {
      ...reuduxactionsReducers,
      ...reduxsauceReducers,
    },
    INITIAL_STATE
  ),
});
